/**
 * The Log in page's rules and words, kept out of the component so they can
 * be tested on their own (LoginScreen.tsx draws the page).
 *
 * OD-B1 (PLAN section 10, HARD): no self-serve signup and no password reset.
 * A chef who is new asks for access; a chef who forgot their password asks
 * their kitchen admin. Nothing here offers either as a form.
 */
import { isApiError } from '@/api/client';

/** Why the server ended the previous session (main.tsx onGateError). */
export type LoginReason = 'signed_out' | 'account_closed';

export const LOGIN_REASONS: readonly LoginReason[] = ['signed_out', 'account_closed'];

export function isLoginReason(v: unknown): v is LoginReason {
  return typeof v === 'string' && (LOGIN_REASONS as readonly string[]).includes(v);
}

/**
 * The note above the form after the server ended a session. Only Pleach can
 * reopen a closed account (the admin screens are staff only), so the closed
 * note points at the contact route the Terms give, not at the kitchen.
 */
export const REASON_TEXT: Record<LoginReason, string> = {
  signed_out:
    'You were logged out. That happens after a password change, or when a session runs out. Log in again to carry on.',
  account_closed:
    'Your account is closed because the updated terms were declined. If that was a mistake, reply to the email your invitation came from.',
};

/**
 * Where "email us" goes. There is no public support inbox yet, so it is
 * null and the forgotten-password line points at the invitation email
 * instead (the Terms name that address as the way to reach Pleach). Set it
 * and the line reads "Ask your kitchen admin, or email us." with a mailto.
 */
export const SUPPORT_EMAIL: string | null = null;

export type LoginFieldErrors = { email?: string; password?: string };

/**
 * The checks made before anything is sent: an empty or clearly wrong email,
 * an empty password. Every attempt that reaches the server counts against
 * the log-in rate limit, so a slip is caught here first. The server stays
 * the judge of whether the pair matches.
 */
export function checkLogin(email: string, password: string): LoginFieldErrors {
  const errors: LoginFieldErrors = {};
  const e = email.trim();
  if (!e || !e.includes('@') || /\s/.test(e)) errors.email = 'Enter the email you log in with.';
  if (!password) errors.password = 'Enter your password.';
  return errors;
}

/**
 * What a failed log-in says. The server's own messages still say "sign-in",
 * so the two codes a chef meets are worded here (D-16: "Log in"); anything
 * else keeps the server's message, which is already written for a person.
 */
export function loginErrorText(err: unknown): string {
  if (isApiError(err)) {
    if (err.code === 'invalid_credentials') {
      return "That email and password don't match. Check them and try again.";
    }
    if (err.code === 'rate_limited') {
      // Two limits answer 429: ten tries a minute from one connection, and
      // ten in 15 minutes against one account.
      return /15 minutes/.test(err.message)
        ? 'Too many tries for this account. Wait 15 minutes, then try again.'
        : 'Too many tries. Wait a minute, then try again.';
    }
    return err.message;
  }
  return 'Could not reach Pleach. Check the connection and try again.';
}

/**
 * The `next` address a chef is sent back to after logging in: a path on this
 * site only. Never protocol-relative ("//evil.example"), never with a
 * backslash (browsers read "/\evil.example" the same way), never with a
 * control character (legacy app.py:1165 guarded the same open redirect).
 * Never /login itself either: a logged-in chef at /login?next=/login would
 * be sent round in a loop.
 */
export function safeNext(next: string | undefined | null): string | null {
  if (!next || !next.startsWith('/') || next.startsWith('//')) return null;
  if (/[\\\u0000-\u001f\u007f]/.test(next)) return null;
  if (/^\/login\/?$/i.test(next.split(/[?#]/)[0])) return null;
  return next;
}
